import React from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import { Edit2, Trash2 } from 'lucide-react';
import { PencilLine } from "lucide-react";

const AssetTable = ({ data, totalPagesServer, currentPage2, pageSize2, onEdit, onDelete }) => {
  const columns = React.useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
        width: '6%',
      },
      {
        Header: 'Name',
        accessor: 'name',
        width: '10%',
      },
      {
        Header: 'Category',
        accessor: 'category',
        width: '10%',
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: 'Status',
        accessor: 'status',
        width: '8%',
        Cell: ({ value }) => (
          <span
            className={`px-2 py-1 rounded-full text-sm ${
              value === 'active'
                ? 'bg-green-100 text-green-800'
                : value === 'inactive'
                ? 'bg-gray-100 text-gray-800'
                : value === 'retired'
                ? 'bg-red-100 text-red-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        ),
      },
      {
        Header: 'Location',
        accessor: 'location',
        width: '8%',
      },
      {
        Header: 'Sensitivity',
        accessor: 'sensitivity_level',
        width: '8%',
        Cell: ({ value }) => (
          <span
            className={`px-2 py-1 rounded-full text-sm ${
              value === 'public'
                ? 'bg-blue-100 text-blue-800'
                : value === 'internal'
                ? 'bg-green-100 text-green-800'
                : value === 'confidential'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        ),
      },
      {
        Header: 'Owner',
        accessor: 'owner_id',
        width: '8%',
        Cell: ({ row }) => (
          <span>
            {row.original.first_name && row.original.last_name
              ? `${row.original.first_name} ${row.original.last_name}`
              : 'Unassigned'}
          </span>
        ),
      },
      {
        Header: 'Confidentiality',
        accessor: 'confidentiality',
        width: '9%',
        Cell: ({ value }) => (
          <div className="relative inline-block">
            <span
              className={`
                px-2 py-1 rounded-full text-sm whitespace-nowrap
                ${
                  value === 'insignificant'
                    ? 'bg-green-100 text-green-800'
                    : value === 'minor'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-red-100 text-red-800'
                }
              `}
            >
              {value ? value[0].toUpperCase() + value.slice(1) : 'Unassessed'}
            </span>

            <div
              className="
                absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
                px-2 py-1 bg-gray-800 text-white text-xs rounded
                opacity-0 hover:opacity-100 transition-opacity duration-200
                whitespace-nowrap z-10
              "
            >
              {value ? value[0].toUpperCase() + value.slice(1) : 'Unassessed'}
            </div>
          </div>
        ),
      },
      {
        Header: 'Integrity',
        accessor: 'integrity',
        width: '9%',
        Cell: ({ value }) => (
          <div className="relative inline-block">
            <span
              className={`
                px-2 py-1 rounded-full text-sm whitespace-nowrap
                ${
                  value === 'insignificant'
                    ? 'bg-green-100 text-green-800'
                    : value === 'minor'
                    ? 'bg-blue-100 text-blue-800'
                    : value === 'moderate'
                    ? 'bg-yellow-100 text-yellow-800'
                    : value === 'major'
                    ? 'bg-orange-100 text-orange-800'
                    : value === 'unassessed'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-red-100 text-red-800'
                }
              `}
            >
              {value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Unassessed'}
            </span>

            <div
              className="
                absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
                px-2 py-1 bg-gray-800 text-white text-xs rounded
                opacity-0 hover:opacity-100 transition-opacity duration-200
                whitespace-nowrap z-10
              "
            >
              {value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Unassessed'}
            </div>
          </div>
        ),
      },
      {
        Header: 'Availability',
        accessor: 'availability',
        className: 'normal-case',
        width: '9%',
        Cell: ({ value }) => (
          <div className="relative inline-block">
            <span
              className={`
                px-2 py-1 rounded-full text-sm whitespace-nowrap
                ${
                  value === 'insignificant'
                    ? 'bg-green-100 text-green-800'
                    : value === 'minor'
                    ? 'bg-blue-100 text-blue-800'
                    : value === 'moderate'
                    ? 'bg-yellow-100 text-yellow-800'
                    : value === 'major'
                    ? 'bg-orange-100 text-orange-800'
                    : value === 'unassessed'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-red-100 text-red-800'
                }
              `}
            >
              {value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Unassessed'}
            </span>

            <div
              className="
                absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
                px-2 py-1 bg-gray-800 text-white text-xs rounded
                opacity-0 hover:opacity-100 transition-opacity duration-200
                whitespace-nowrap z-10
              "
            >
              {value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Unassessed'}
            </div>
          </div>
        ),
      },
      {
  Header: 'Actions',
  id: 'actions',
  width: '6%',
  Cell: ({ row }) => (
    <div
      className="flex items-center justify-end gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Pen edit icon */}
      <button
        type="button"
        onClick={() => onEdit(row.original)}
        className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600"
      >
        <PencilLine className="w-4 h-4" />
      </button>

      {/* Trash icon */}
      <button
        type="button"
        onClick={() => onDelete(row.original)}
        className="p-1.5 rounded-md hover:bg-red-50 text-red-600"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  ),
}

    ],
    [onEdit, onDelete]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: data || [],
      initialState: { pageIndex: 0 },
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="relative rounded-2xl shadow-lg border border-gray-200 bg-white">
      {/* rounded container so header & rows inherit corners */}
      <div className="overflow-x-auto rounded-2xl overflow-hidden">
        <table
          {...getTableProps()}
          className="min-w-full bg-white"
        >
          <thead className="bg-gray-50">
            {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                        <th
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            className={`px-6 py-3 text-sm font-medium text-gray-500 tracking-wider whitespace-nowrap
              ${column.id === "actions" ? "text-center" : "text-left cursor-pointer"}`}
                            style={{ width: column.width }}
                        >
                            {column.render("Header")}
                            <span>
                                {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                            </span>
                        </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            {...getTableBodyProps()}
            className="bg-white divide-y divide-gray-200"
          >
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps({
                    onClick: () => onEdit(row.original),
                  })}
                  className="group cursor-pointer transition-colors duration-200 hover:bg-gray-50"
                >
                  {row.cells.map((cell) => (
                   <td
  {...cell.getCellProps()}
  className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500
              ${cell.column.id === "actions" ? "text-right" : ""}`}
>
  {cell.render("Cell")}
</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AssetTable;
